
"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";

export default function PageVisitTracker() {
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const lastTrackedRef = useRef<string>("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (status !== "authenticated" || !session?.user || !pathname) return;

    // Client-side dedup: don't re-track the exact same page
    if (lastTrackedRef.current === pathname) return;

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce: wait 1 second before sending to avoid logging rapid navigations
    debounceTimerRef.current = setTimeout(() => {
      lastTrackedRef.current = pathname;

      fetch("/api/track-visit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pathname }),
        keepalive: true,
      }).catch(() => {
        // Silently fail - tracking should never break the UX
      });
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [pathname, session, status]);

  return null;
}
